import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormattingContext } from '../../context/FormattingContext';
import { selectorsCart } from '../../redux/cart/cartReducer';
import { selectorsLocale } from '../../redux/locale/localeReducer';
import { buildLocale } from '../../utils/buildLocale';
import CartItemComponent from './CartItemComponent';
import ShipmentModal from '../Modals/ShipmentModal';
import { selectorsDelivery } from '../../redux/delivery/deliveryReducer';
import { formatWeightWithType, SmallUnits } from '../../utils/calculateUnits';
import ProgressBarComponent from '../common/ProgressBarComponent';
import AnimatedCounter from '../common/AnimatedCounter';

const CartList = () => {
  const items: any = useSelector(selectorsCart.getCartDataProducts);
  const notAvailableProducts = useSelector(selectorsCart.getNotAvailableProducts);
  const currentTranslate = useSelector(selectorsLocale.getTranslate);
  const cart = useSelector(selectorsCart.getCart);
  const { formatPrice } = useFormattingContext();

  const productsPrice = cart?.productsPrice ? +cart.productsPrice : 0;
  const bottleTotalDeposit = cart?.bottleTotalDeposit ? +cart.bottleTotalDeposit : 0;
  const rackTotalDeposit = cart?.rackTotalDeposit ? +cart.rackTotalDeposit : 0;
  const deliveryCost = cart?.deliveryCost ? +cart.deliveryCost : 0;
  const totalWeightDeposit = cart?.totalWeightDeposit ? +cart.totalWeightDeposit : 0;
  const total = cart?.total ? +cart.total : 0;
  const totalWeight = cart?.totalWeight ? +cart.totalWeight : 0;

  const isInCourierArea = useSelector(selectorsDelivery.isInCourierArea);
  const priceDiffForCheaperDelivery = useSelector(selectorsDelivery.getPriceDiffForCheaperDelivery(productsPrice));
  const currentDeliveryCostForPriceData = useSelector(
    selectorsDelivery.getCurrentDeliveryCostDataForPrice(productsPrice),
  );
  const prevDeliveryCostForPrice = useSelector(selectorsDelivery.getPrevDeliveryCostForPrice(productsPrice));
  const [isOpenShipmentModal, setIsOpenShipmentModal] = useState(false);

  const [deliveryCostStartValue, setDeliveryCostStartValue] = useState(0);
  const [deliveryCostEndValue, setDeliveryCostEndValue] = useState(0);

  const MAX_BOX_WEIGHT = 30000;

  const getDeliveryCostStartValue = () => {
    if (priceDiffForCheaperDelivery === null) {
      return prevDeliveryCostForPrice || 0;
    }

    if (isInCourierArea) {
      return deliveryCost;
    }

    return currentDeliveryCostForPriceData !== null ? currentDeliveryCostForPriceData.price || 0 : 0;
  };

  const getDeliveryCostEndValue = () => {
    if (priceDiffForCheaperDelivery === null) {
      return currentDeliveryCostForPriceData !== null ? currentDeliveryCostForPriceData.price || 0 : 0;
    }

    return priceDiffForCheaperDelivery?.nextDeliveryCost || 0;
  };

  const getFreeBoxDeliveryMessage = () => {
    if (isInCourierArea) {
      return null;
    }

    if (priceDiffForCheaperDelivery === null) {
      return (currentDeliveryCostForPriceData?.price || 0) === getDeliveryCostStartValue() &&
        currentDeliveryCostForPriceData?.hasFreeBox === true
        ? ` <b>${buildLocale(currentTranslate, 'cartOneFreeBoxMessage')}</b>`
        : null;
    }

    return (priceDiffForCheaperDelivery?.nextDeliveryCost || 0) === getDeliveryCostStartValue() &&
      priceDiffForCheaperDelivery?.hasFreeBox === true
      ? ` <b>${buildLocale(currentTranslate, 'cartOneFreeBoxMessage')}</b>`
      : null;
  };

  const freeBoxDeliveryMessage = getFreeBoxDeliveryMessage();
  const currentBoxCount = !isInCourierArea ? Math.ceil(totalWeight / MAX_BOX_WEIGHT) : 0;
  const prevBoxCount = currentBoxCount > 0 ? currentBoxCount - 1 : currentBoxCount;
  const prevFilledBoxWeight = prevBoxCount * MAX_BOX_WEIGHT;
  const currentPartiallyFilledBoxWeight = totalWeight - prevFilledBoxWeight;

  return (
    <div className="basket-page_content">
      <div className="basket-page_list">
        {items?.length > 0 &&
          items.map((item, index: number) => {
            const isNotAvailable =
              notAvailableProducts && notAvailableProducts.find((p) => p.product.id === item.product.id);
            const setAvailableType = () => {
              if (!isNotAvailable) {
                return false;
              } else if (isNotAvailable && isNotAvailable.count === 1) {
                return 'noProduct';
              } else {
                return 'count';
              }
            };
            return (
              <CartItemComponent
                item={item}
                notAvailableType={setAvailableType()}
                key={item.product.id}
                dlItemIndex={index}
              />
            );
          })}
      </div>
      <div className="basket-page_total">
        {bottleTotalDeposit > 0 && (
          <p className="basket-block_bottle">
            <span>{buildLocale(currentTranslate, 'cartTotalBottleDeposit')}</span>
            <span>{formatPrice(bottleTotalDeposit)}</span>
          </p>
        )}
        {rackTotalDeposit > 0 && (
          <p className="basket-block_bottle">
            <span>{buildLocale(currentTranslate, 'cartTotalRackDeposit')}</span>
            <span>{formatPrice(rackTotalDeposit)}</span>
          </p>
        )}
        {deliveryCost >= 0 && (
          <p className="basket-block_bottle shipment" onClick={() => setIsOpenShipmentModal(true)}>
            <span>{buildLocale(currentTranslate, 'btnDelivery')}</span>
            <span>{formatPrice(deliveryCost)}</span>
          </p>
        )}

        <>
          {!!deliveryCost && !!priceDiffForCheaperDelivery && (
            <p
              className="basket-block_bottle spend-more"
              dangerouslySetInnerHTML={{
                __html: `<span>${buildLocale(currentTranslate, 'spendMoreAndGetCheaperDelivery', {
                  '{{AMOUNT}}': formatPrice(priceDiffForCheaperDelivery.priceDiff, false),
                })}${!!freeBoxDeliveryMessage?.length ? freeBoxDeliveryMessage : ''}</span>`,
              }}></p>
          )}
          <ProgressBarComponent
            wrapperClassName="basket-progress-bar"
            startProgressElement={
              <span className="endpoint-progress-label">
                <>
                  <AnimatedCounter
                    newValue={getDeliveryCostStartValue()}
                    prevValue={deliveryCostStartValue}
                    updatedValue={deliveryCostStartValue}
                    updateValue={setDeliveryCostStartValue}
                  />
                  {!isInCourierArea && (
                    <span className="not-in-courier-area">
                      /<i className="fa fa-archive"></i>
                    </span>
                  )}
                </>
              </span>
            }
            endProgressElement={
              <span className="endpoint-progress-label endpoint-end">
                <>
                  <AnimatedCounter
                    newValue={getDeliveryCostEndValue()}
                    prevValue={deliveryCostEndValue}
                    updatedValue={deliveryCostEndValue}
                    updateValue={setDeliveryCostEndValue}
                  />
                  {!isInCourierArea && (
                    <>
                      <span className="not-in-courier-area">
                        /<i className="fa fa-archive"></i>
                      </span>
                      {!!freeBoxDeliveryMessage?.length && (
                        <span className="not-in-courier-area">
                          +<i className="fa fa-gift"></i>
                        </span>
                      )}
                    </>
                  )}
                </>
              </span>
            }
            completedClassName="progress-bar progress-bar-striped label-end"
            labelSize="1.4rem"
            isLabelVisible={true}
            customLabel={`<i class="fa fa-truck fa-flip-horizontal"></i>`}
            completed={productsPrice}
            maxCompleted={productsPrice + (priceDiffForCheaperDelivery?.priceDiff || 0)}
          />
        </>
        {!isInCourierArea && !!cart?.totalWeight && (
          <>
            <p className="basket-block_bottle order-weight">
              <span>{buildLocale(currentTranslate, 'weightOfYourShoppingCart')}:</span>
              <span>
                <span>{formatWeightWithType(SmallUnits.g, '', String(totalWeight))}</span>
                <span className="order-weight-package">
                  {currentBoxCount} x <span>{buildLocale(currentTranslate, 'commonPacket')}</span>
                </span>
              </span>
            </p>
            <ProgressBarComponent
              wrapperClassName="basket-progress-bar"
              startProgressElement={
                <span className="endpoint-progress-label bg-revert">
                  <>
                    {currentBoxCount}
                    <span className="not-in-courier-area">
                      x<i className="fa fa-archive"></i>
                    </span>
                  </>
                </span>
              }
              endProgressElement={
                <span className="endpoint-progress-label endpoint-end bg-revert">
                  <>
                    {currentBoxCount + 1}
                    <span className="not-in-courier-area">
                      x<i className="fa fa-archive"></i>
                    </span>
                  </>
                </span>
              }
              completedClassName="progress-bar progress-bar-striped label-end bg-revert"
              customLabel={`<i class="fa fa-truck fa-flip-horizontal"></i>`}
              completed={currentPartiallyFilledBoxWeight}
              maxCompleted={MAX_BOX_WEIGHT}
              baseBgColor={'rgba(11, 74, 135, 0.4)'}
            />
          </>
        )}
        {isOpenShipmentModal && (
          <ShipmentModal isOpen={isOpenShipmentModal} closeModal={() => setIsOpenShipmentModal(false)} />
        )}
        {totalWeightDeposit > 0 && (
          <p className="basket-block_bottle">
            <span>{buildLocale(currentTranslate, 'cartTotalWeightDeposit')}</span>
            <span>{formatPrice(totalWeightDeposit)}</span>
          </p>
        )}
        {total && (
          <p className="total">
            <span>{buildLocale(currentTranslate, 'mobileCouponAccepted')}</span>
            <span>{formatPrice(total)}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default CartList;
